// Tab definitions for the workflow table. Column lists are ported from V1's
// column groups (SpreadsheetView/utils/columnGroups.ts getTifpaColumnGroups)
// and must use the exact column display names — they double as row_data keys.
// "Legal Business Name" and each tab's status field (config/tabStatus.ts)
// are pinned outside the tabs.

import { TAB_STATUS_COLUMNS } from './tabStatus';
import { ON_DEMAND_BILLING_COLUMNS } from './onDemandBilling';
import { TAX_FILE_READY_COLUMNS } from './taxFileReady';

export interface WorkflowTab {
  id: string;
  label: string;
  columns: string[];
  /** Tailwind classes for the active tab underline/accent */
  accentClass: string;
  /**
   * Client service flag (clients table, merged into row_data by the loader)
   * this tab filters rows by — only accounts WITH the service show. Absent =
   * the tab shows every account.
   */
  serviceColumn?: string;
}

export const WORKFLOW_TABS: WorkflowTab[] = [
  {
    // Detail-sheet section only — the table view filters this tab out
    id: 'info',
    label: 'Info',
    accentClass: 'border-slate-500 text-slate-700',
    columns: [
      'Account Order',
      'Federal Tax ID',
      'Address',
      'City',
      'State',
      'ZIP Code',
      'Owner Name',
      'Owner Phone',
      'Owner Email',
      'Sales Tax',
      'Payroll',
      'Bookkeeping',
      'ACH Payor',
      '1099',
      'S Corp Election',
      'QB Process',
    ],
  },
  {
    // Table-only overview: every account, no service filter. The detail
    // sheet skips this tab (its fields all live in other sections).
    id: 'all-accounts',
    label: 'All Workflow Accounts',
    accentClass: 'border-indigo-500 text-indigo-700',
    columns: [
      'ACH Payor',
      'Bookkeeping',
      'Sales Tax',
      'Payroll',
      '1099',
      'S Corp Election',
      'Account Manager',
      'Bookkeeper',
      'QB Process',
      'Sales Taxes Assigned To',
      'Payroll Taxes Assigned To',
      'Payroll Frequency',
    ],
  },
  {
    id: 'bookkeeping',
    label: 'Bookkeeping',
    accentClass: 'border-sky-500 text-sky-700',
    serviceColumn: 'Bookkeeping',
    columns: [
      'Account Manager',
      'Bookkeeper',
      'Last QB & Statement Downloaded',
      'Saved To Revver Date',
      'Saved To Dropbox Date',
      'Statements Booked',
      'QB-Bank Statements Booked',
      'Manual Receipts Booked',
      'Ask My Accountant',
      'Prelim Reviewed By Asst Manager',
      'Reviewed by Account Manager',
      'Ready For Taxes',
    ],
  },
  {
    id: 'sales-tax',
    label: 'Sales Tax',
    accentClass: 'border-emerald-500 text-emerald-700',
    serviceColumn: 'Sales Tax',
    columns: [
      'QB-Bank Statements Booked',
      'Manual Receipts Booked',
      'Sales Taxes Assigned To',
      'Sales Tax Reported',
    ],
  },
  {
    id: 'payroll',
    label: 'Payroll',
    accentClass: 'border-violet-500 text-violet-700',
    serviceColumn: 'Payroll',
    columns: [
      'Payroll Taxes Assigned To',
      'Payroll Frequency',
      'Last Payroll Paid',
      // 2026-10-04: State WTH, 941, State UI and 940 each in their own
      // column (previously stacked pairs); only W3 W2 Fed/State stays
      // merged (WorkflowTable MERGED_COLUMNS)
      'State WTH',
      '941',
      'State UI',
      '940 Annual Payroll Report',
      'W3 W2 Efile Fed',
      'W3 W2 Efile State',
    ],
  },
  {
    id: 'tifpa-tasks',
    label: 'TIFPA Tasks',
    accentClass: 'border-rose-500 text-rose-700',
    columns: [
      'Last Financial Printout',
      'Financial Statements Ready For Pickup',
      'Financial Statements Ready for Pick Up',
      'Financial Statements Ready',
      'Last Customer Pickup',
      'Pickup Date',
      'QB Update',
    ],
  },
  {
    // Client-backed virtual columns (sql/workflow-v2/011) — which tax
    // documents are in hand for the client's tax file. No service filter:
    // every account shows.
    id: 'tax-file-ready',
    label: 'Tax File Ready',
    accentClass: 'border-teal-500 text-teal-700',
    columns: [...TAX_FILE_READY_COLUMNS],
  },
  {
    // Client-backed virtual columns (sql/workflow-v2/012) — one-off billable
    // work items, stamped {date, username} when performed. No service
    // filter: every account shows.
    id: 'on-demand-billing',
    label: 'On Demand Billing',
    accentClass: 'border-fuchsia-500 text-fuchsia-700',
    columns: [...ON_DEMAND_BILLING_COLUMNS],
  },
];

// FIXED width per column, by exact name first, then by column type. The
// table uses table-fixed layout, so the header row's width classes ARE the
// column widths — every column must resolve to a fixed w-* here (min-w
// would be ignored). Keeps the grid stable across tabs and months.
const COLUMN_WIDTH_BY_NAME: Record<string, string> = {
  // The virtual stacked Account Manager + Bookkeeper table column
  'Bookkeeper / Acct Mgr': 'w-[200px]',
  // The virtual stacked Payroll Taxes Assigned To + Payroll Frequency column
  'Assigned To / Frequency': 'w-[200px]',
  // The virtual stacked completion-date pairs (WorkflowTable MERGED_COLUMNS)
  'Downloaded / Revver': 'w-[160px]',
  'Statements / Receipts Booked': 'w-[160px]',
  'Prelim / Acct Mgr Reviewed': 'w-[160px]',
  'W3 W2 Efile Fed / State': 'w-[160px]',
  // Wider text row-labels (Fin Stmnt / Pickup) need the extra room
  'Customer Pickup / Date': 'w-[180px]',
  // Short Set…/Online/Manual split button
  'QB Process': 'w-[110px]',
  // Sales Tax Reported's inline sub-columns (WorkflowTable SALES_TAX_FIELDS)
  Status: 'w-[150px]',
  'Date Reported': 'w-[130px] text-center',
  Amount: 'w-[130px]',
  Notes: 'w-[200px]',
  'Account Order': 'w-[50px] text-center',
  Address: 'w-[220px]',
  City: 'w-[110px]',
  State: 'w-20',
  'ZIP Code': 'w-24',
  'Owner Name': 'w-[160px]',
  'Owner Phone': 'w-[130px]',
  'Owner Email': 'w-[200px]',
  'Federal Tax ID': 'w-[120px]',
  // Flag icon only — keep it tight and centered
  'Ask My Accountant': 'w-24 text-center',
  // 1099K multi-toggle chip lists the selected platforms
  '1099K': 'w-[160px]',
  // Three-line headers need a touch more room than the boolean default
  'BROILER STATEMENT REPORT': 'w-28 text-center',
  // On Demand Billing free text (amounts/notes)
  '1099s': 'w-[180px]',
};

const COLUMN_WIDTH_BY_TYPE: Record<string, string> = {
  // ✓/— toggle; w-24 (not tighter) so one-word headers like
  // BOOKKEEPING don't overflow the column
  boolean: 'w-24 text-center',
  // Split button: avatar + MM/DD/YY + chevron
  date_with_user: 'w-[130px] text-center',
  month_with_user: 'w-[130px] text-center',
  month_sales_data: 'w-[150px]',
  employee_assignee: 'w-[160px]',
  multi_employee_assignee: 'w-[180px]',
  // Pill select; longest label is Semi-Monthly
  payroll_frequency: 'w-[140px]',
};

/**
 * Display labels for table column headers. The underlying column names
 * double as row_data keys (and virtual-column ids) and must not change —
 * this is presentation only. '\n' renders as a line break in the header
 * (whitespace-pre-line); other surfaces collapse it to a space.
 */
const COLUMN_HEADER_LABELS: Record<string, string> = {
  'Account Order': 'Acct Order',
  'Bookkeeper / Acct Mgr': 'Bookkeeper\nAcct Mgr',
  'Sales Taxes Assigned To': 'ST Manager',
  'Assigned To / Frequency': 'PR Manager\nFrequency',
  'Downloaded / Revver': 'Bank Data DL\nSaved to Dropbox',
  'Statements / Receipts Booked': 'Bank Data Booked\nManual Receipts Booked',
  'Prelim / Acct Mgr Reviewed': 'Prelim Review\nFinal Review',
  // The Sales Tax Reported inline sub-column
  Status: 'Business Status',
  'Last Payroll Paid': 'Last Paid',
  // Standalone payroll report columns (month_with_user, w-[130px])
  '940 Annual Payroll Report': '940 Annual',
  'W3 W2 Efile Fed / State': 'W3 W2 Efiled\n(Fed / State)',
  'Customer Pickup / Date': 'Fin Stmnt Date Picked Up\nClient Pick Up Date',
  'Last Financial Printout': 'Fin Stmnts Printed',
  // Two words per line so the boolean column's w-24 doesn't overflow
  'S Corp Election': 'S Corp\nElection',
  // Whichever live-name variant the month's sheet uses
  'Financial Statements Ready For Pickup': 'Fin Stmnts Ready for Pickup',
  'Financial Statements Ready for Pick Up': 'Fin Stmnts Ready for Pickup',
  // Tax File Ready booleans: two words per line so w-24 doesn't overflow
  'ENDING INVENTORY': 'Ending\nInventory',
  'RENTAL PROPERTY': 'Rental\nProperty',
  'BROILER STATEMENT REPORT': 'Broiler\nStmnt Report',
  'STOCK STATEMENT': 'Stock\nStmnt',
};

export function columnHeaderLabel(name: string): string {
  return COLUMN_HEADER_LABELS[name] ?? name;
}

export function columnWidthClass(name: string, columnType: string): string {
  return (
    COLUMN_WIDTH_BY_NAME[name] ??
    COLUMN_WIDTH_BY_TYPE[columnType] ??
    'w-[130px]'
  );
}

/** Pinned outside the tabs, plus columns that should never render */
const CLAIMED_ELSEWHERE = new Set([
  'Legal Business Name',
  // Account Status / Sales Tax Status / Payroll Status
  ...TAB_STATUS_COLUMNS,
  'Notes',
  // Removed 2026-08-14 (sql/workflow-v2/010 drops the column + data); guard
  // kept so a stale backup can never resurface it in the "Other" tab
  'Book Completed By',
  // Hidden 2026-08-06: redundant once the merged review columns (Prelim /
  // Acct Mgr Reviewed) timestamp the review dates
  'Reviewed Up To',
]);

/**
 * The static tabs plus a catch-all "Other" tab for any live column not
 * claimed by a tab. Column names have drifted over the workflow's history
 * (renames via UI/SQL, e.g. "Saved To Revver Date" → "Statements Booked"),
 * so this guarantees nothing in the spreadsheet is invisible — and shows us
 * the real live names.
 */
export function buildTabs(liveColumnNames: string[]): WorkflowTab[] {
  const claimed = new Set(CLAIMED_ELSEWHERE);
  WORKFLOW_TABS.forEach(tab => tab.columns.forEach(c => claimed.add(c)));
  const other = liveColumnNames.filter(name => !claimed.has(name));
  if (other.length === 0) return WORKFLOW_TABS;
  return [
    ...WORKFLOW_TABS,
    {
      id: 'other',
      label: 'Other',
      accentClass: 'border-amber-500 text-amber-700',
      columns: other,
    },
  ];
}
